import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CheckCircle2, CircleDashed, ClipboardList, Clapperboard, Film, FolderHeart, Hand, Play, Plus, RotateCcw, Save, Shield, Trash2, Undo2, User, Users, XCircle } from 'lucide-react';
import { TASK_STATUS_LABELS, type TaskDTO, type TaskStatus } from '@prism/contracts';
import { api, ApiError, errorMessage } from '../api/client';
import { useCollectionItems, useMe, useTaskNotes, useTaskSummary, useTasks } from '../api/hooks';
import { ApprovalsView } from './Approvals';
import { AssigneeSelect, NewTaskDialog, assigneeBody, assigneeValue, toLocalInput } from '../components/AssignTask';
import { toast } from '../components/Toasts';
import { NoteComposer, Thread } from '../components/Notes';
import { Segmented } from '../components/Segmented';
import { dateTime, relative } from '../lib/format';
import { queryKeys } from '../api/queryKeys';
import { LoadMore } from '../components/LoadMore';
import { AutoGrowTextarea } from '../components/AutoGrowTextarea';
import { detailParts } from '../lib/detailLinks';
import { changedFields, fieldList, rebaseTaskForm, type TaskField, type TaskForm } from './taskForm';

/** A task's editable fields as the edit form holds them. */
const formOf = (t: TaskDTO): TaskForm => ({ title: t.title, details: t.details ?? '', assignee: assigneeValue(t.assignee), due: toLocalInput(t.dueAt) });

const STATUS_ICON: Record<TaskStatus, React.ReactNode> = {
  open: <CircleDashed size={14} color="var(--muted)" />, in_progress: <Play size={14} color="var(--processing)" />,
  done: <CheckCircle2 size={14} color="var(--ready)" />, cancelled: <XCircle size={14} color="var(--faint)" />,
};
const SUBJECT_ICON = { asset: <Film size={13} />, sequence: <Clapperboard size={13} />, collection: <FolderHeart size={13} /> } as const;
/** A person, a group or (1.0.0-rc.9) a role. */
const ASSIGNEE_ICON = { user: <User size={11} />, group: <Users size={11} />, role: <Shield size={11} /> } as const;

function Due({ t }: { t: TaskDTO }) {
  if (!t.dueAt) return null;
  return (
    <span className={`task-due ${t.overdue ? 'status failed' : 'faint'}`} title={dateTime(t.dueAt)} style={{ fontSize: 12 }}>
      {t.overdue ? <AlertTriangle size={11} /> : <CalendarClock size={11} />} {t.overdue ? 'Overdue ' : 'Due '}{relative(t.dueAt)}
    </span>
  );
}

/** Tasks assigned to you (or your groups), that you created, or everyone's for administrators. */
export function Tasks() {
  const me = useMe().data!;
  const [params, setParams] = useSearchParams();
  const view = params.get('view');
  const scope = (params.get('scope') ?? 'mine') as 'mine' | 'created' | 'all';
  const status = (params.get('status') ?? 'active') as 'active' | 'done' | 'all';
  // Assigned to me (1.0.0-rc.9): mine directly, through my roles and groups, or both.
  const assigned = (params.get('assigned') ?? 'any') as 'me' | 'shared' | 'any';
  const subject = { assetId: params.get('asset') ?? undefined, sequenceId: params.get('sequence') ?? undefined, collectionId: params.get('collection') ?? undefined };
  const q = useTasks({ scope, status, ...(scope === 'mine' ? { assigned } : {}), ...subject }, view !== 'approvals');
  const items = q.data?.items ?? [];
  const selectedId = params.get('task') ?? items[0]?.id ?? null;
  const selected = items.find((t) => t.id === selectedId) ?? null;
  const [creating, setCreating] = useState(false);
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v === null ? next.delete(k) : next.set(k, v));
    setParams(next, { replace: true });
  };
  const filtered = Boolean(subject.assetId || subject.sequenceId || subject.collectionId);
  const summary = useTaskSummary().data;
  const pendingApprovals = (summary?.approvals ?? 0) + (summary?.unseen ?? 0);
  // Tasks or (0.31.0) approvals: workflow steps, share links and requests from people, each with a thread.
  const viewSwitch = (
    <Segmented role="tablist" label="Tasks or approvals" value={view === 'approvals' ? 'approvals' : 'tasks'}
      onChange={(v) => setParams(new URLSearchParams(v === 'approvals' ? { view: 'approvals' } : {}), { replace: true })} options={[
        { value: 'tasks', label: 'Tasks', testId: 'view-tasks' },
        { value: 'approvals', label: <><Hand size={13} /> Approvals{pendingApprovals ? ` (${pendingApprovals})` : ''}</>, testId: 'view-approvals' },
      ]} />
  );
  if (view === 'approvals') {
    return (
      <>
        <header className="topbar">
          <h1><ClipboardList size={16} color="var(--accent-strong)" /> Tasks</h1>
          {viewSwitch}
        </header>
        <ApprovalsView />
      </>
    );
  }

  return (
    <>
      <header className="topbar">
        <h1><ClipboardList size={16} color="var(--accent-strong)" /> Tasks</h1>
        {viewSwitch}
        <div className="segmented" role="tablist" aria-label="Whose tasks">
          <button aria-pressed={scope === 'mine' && !filtered} onClick={() => set({ scope: null, asset: null, sequence: null, collection: null, task: null })} data-testid="tasks-mine">Assigned to me</button>
          <button aria-pressed={scope === 'created' && !filtered} onClick={() => set({ scope: 'created', assigned: null, asset: null, sequence: null, collection: null, task: null })} data-testid="tasks-created">Created by me</button>
          <button aria-pressed={scope === 'all' && !filtered} onClick={() => set({ scope: 'all', assigned: null, asset: null, sequence: null, collection: null, task: null })}>{me.permissions.includes('admin') ? 'All' : 'Mine and created'}</button>
        </div>
        <Segmented role="tablist" label="Status" value={status} onChange={(v) => set({ status: v === 'active' ? null : v, task: null })} options={[
          { value: 'active', label: 'Open' }, { value: 'done', label: 'Finished' }, { value: 'all', label: 'All' },
        ]} />
        <div className="topbar-spacer" />
        <button className="btn primary" onClick={() => setCreating(true)} data-testid="new-task"><Plus size={15} /> New task</button>
      </header>
      <div className="tasks-layout">
        <div className="task-list" data-testid="task-list">
          {scope === 'mine' && !filtered && (
            <div className="task-chips" role="group" aria-label="Assigned how" data-testid="task-chips">
              {([['me', 'Mine'], ['shared', 'My roles and groups'], ['any', 'All']] as const).map(([v, label]) => (
                <button key={v} className={`btn sm ${assigned === v ? '' : 'ghost'}`} aria-pressed={assigned === v} onClick={() => set({ assigned: v === 'any' ? null : v, task: null })} data-testid={`task-chip-${v}`}>{label}</button>
              ))}
            </div>
          )}
          {filtered && <div className="banner info" style={{ margin: 8 }}>Showing the tasks about one {subject.assetId ? 'asset' : subject.sequenceId ? 'edit' : 'collection'}. <button className="btn ghost sm" onClick={() => set({ asset: null, sequence: null, collection: null })}>Show all</button></div>}
          {q.isLoading ? <div className="skeleton" style={{ height: 120, margin: 12 }} /> : items.length === 0 ? (
            <p className="muted" style={{ padding: 16 }}>{scope === 'mine' && status === 'active' ? 'Nothing assigned to you right now.' : 'No tasks here.'}</p>
          ) : items.map((t) => (
            <button key={t.id} className={`task-row ${t.id === selectedId ? 'active' : ''}`} onClick={() => set({ task: t.id })} data-testid="task-row">
              {STATUS_ICON[t.status]}
              <span className="task-row-main">
                <strong>{t.title}</strong>
                <span className="faint task-row-sub" title={`${t.subject ? `${t.subject.name} · ` : ''}${t.assignee?.name ?? 'Unassigned'}`}>
                  {t.subject && <>{SUBJECT_ICON[t.subject.type]} {t.subject.name} · </>}
                  {t.assignee ? <>{ASSIGNEE_ICON[t.assignee.type]} {t.assignee.name}</> : 'Unassigned'}
                </span>
              </span>
              <Due t={t} />
            </button>
          ))}
          <LoadMore list={q} shown={items.length} noun="tasks" />
        </div>
        {selected ? <TaskDetail key={selected.id} t={selected} onMoved={(to) => {
          // A task taken (or given back) leaves the chip it was under: follow it.
          if (scope === 'mine' && assigned !== 'any' && assigned !== to) set({ assigned: to, task: selected.id });
        }} /> : <div className="page muted">Choose a task.</div>}
      </div>
      {creating && <NewTaskDialog subject={null} onClose={() => setCreating(false)} />}
    </>
  );
}

function TaskDetail({ t, onMoved }: { t: TaskDTO; onMoved?: (to: 'me' | 'shared') => void }) {
  const qc = useQueryClient();
  const notes = useTaskNotes(t.id);
  const [note, setNote] = useState('');
  // The version the form started from (1.0.0-rc.14, #12): a change of the task meanwhile keeps what is being typed
  // (untouched fields follow it), and Save sends only the changed fields, checked against this version's updatedAt.
  const [base, setBase] = useState(() => ({ form: formOf(t), updatedAt: t.updatedAt }));
  const [edit, setEdit] = useState<TaskForm>(base.form);
  const [clash, setClash] = useState<TaskField[]>([]);
  const editRef = useRef(edit);
  editRef.current = edit;
  useEffect(() => {
    if (t.updatedAt === base.updatedAt) return;
    const next = formOf(t);
    const r = rebaseTaskForm(base.form, editRef.current, next);
    setEdit(r.edit);
    // A field changed here and there: the form keeps the old version, so Save is refused (409) and says so.
    if (r.conflicts.length) setClash(r.conflicts);
    else setBase({ form: next, updatedAt: t.updatedAt });
  }, [t.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  const changed = changedFields(base.form, edit);
  const dirty = changed.length > 0;
  const shared = t.subject?.type === 'collection' && t.subject.link.startsWith('/tasks');
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: queryKeys.tasksAll() }), qc.invalidateQueries({ queryKey: queryKeys.taskNotes(t.id) })]);
  const patch = async (body: Record<string, unknown>, ok: string) => {
    try {
      await api(`/api/v1/tasks/${t.id}`, { method: 'PATCH', json: body });
      await refresh();
      toast(ok);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  /** Save changes: only the changed fields, with the updatedAt of the version the form started from. */
  const saveEdit = async () => {
    const body: Record<string, unknown> = { expectedUpdatedAt: base.updatedAt };
    for (const f of changed) {
      if (f === 'title') body.title = edit.title;
      if (f === 'details') body.details = edit.details;
      if (f === 'assignee') Object.assign(body, assigneeBody(edit.assignee));
      if (f === 'due') body.dueAt = edit.due ? new Date(edit.due).toISOString() : null;
    }
    try {
      const saved = await api<TaskDTO>(`/api/v1/tasks/${t.id}`, { method: 'PATCH', json: body });
      setBase({ form: formOf(saved), updatedAt: saved.updatedAt });
      setEdit(formOf(saved));
      setClash([]);
      await refresh();
      toast('Saved');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'TASK_CHANGED') {
        // Someone changed the task meanwhile: take their version, keep what was typed here, and let Save overwrite.
        try {
          const cur = await api<TaskDTO>(`/api/v1/tasks/${t.id}`);
          const r = rebaseTaskForm(base.form, editRef.current, formOf(cur));
          setBase({ form: formOf(cur), updatedAt: cur.updatedAt });
          setEdit(r.edit);
          setClash([]);
          toast(r.conflicts.length
            ? `Someone else changed ${fieldList(r.conflicts)} while you were editing. Your version is still here: check it, then save again.`
            : 'Someone else changed this task while you were editing. Their changes are in the form with yours: save again.', 'error');
          void refresh();
        } catch (e) {
          toast(errorMessage(e), 'error');
        }
        return;
      }
      toast(errorMessage(err), 'error');
    }
  };
  const active = t.status === 'open' || t.status === 'in_progress';
  // Take it / Give back (1.0.0-rc.9): a role's or group's task becomes one person's, and back.
  const act = async (what: 'take' | 'give-back', ok: string) => {
    try {
      await api(`/api/v1/tasks/${t.id}/${what}`, { method: 'POST', json: {} });
      onMoved?.(what === 'take' ? 'me' : 'shared');
      await refresh();
      toast(ok);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };

  return (
    <div className="page task-detail" data-testid="task-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {STATUS_ICON[t.status]}
        <span className="pill" data-testid="task-status">{TASK_STATUS_LABELS[t.status]}</span>
        <Due t={t} />
        <span style={{ flex: 1 }} />
        {t.canTake && <button className="btn sm" onClick={() => void act('take', `Taken from ${t.assignee?.name ?? 'the group'}: it is yours now`)} data-testid="task-take"><Hand size={13} /> Take it</button>}
        {t.canGiveBack && <button className="btn ghost sm" onClick={() => void act('give-back', `Given back to ${t.takenFrom?.name ?? 'the group'}`)} data-testid="task-give-back"><Undo2 size={13} /> Give back</button>}
        {t.canChangeStatus && t.status === 'open' && <button className="btn sm" onClick={() => void patch({ status: 'in_progress' }, 'Started')} data-testid="task-start"><Play size={13} /> Start</button>}
        {t.canChangeStatus && active && <button className="btn primary sm" onClick={() => void patch({ status: 'done' }, 'Done')} data-testid="task-done"><CheckCircle2 size={13} /> Mark done</button>}
        {t.canChangeStatus && !active && <button className="btn sm" onClick={() => void patch({ status: 'open' }, 'Reopened')}><RotateCcw size={13} /> Reopen</button>}
        {t.canEdit && active && <button className="btn ghost sm" onClick={() => void patch({ status: 'cancelled' }, 'Cancelled')}><XCircle size={13} /> Cancel task</button>}
        {t.canEdit && <button className="btn ghost sm danger" aria-label="Delete task" onClick={() => {
          if (!window.confirm(`Delete “${t.title}” and its notes?`)) return;
          void api(`/api/v1/tasks/${t.id}`, { method: 'DELETE' }).then(refresh, (err) => toast(errorMessage(err), 'error'));
        }}><Trash2 size={13} /></button>}
      </div>

      {t.canEdit ? (
        <>
          <input className="input task-title-input" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} aria-label="Task" />
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field"><label htmlFor="t-assignee">Assigned to</label><AssigneeSelect id="t-assignee" value={edit.assignee} onChange={(v) => setEdit({ ...edit, assignee: v })} /></div>
            <div className="field"><label htmlFor="t-due">Deadline</label><input id="t-due" className="input" type="datetime-local" value={edit.due} onChange={(e) => setEdit({ ...edit, due: e.target.value })} /></div>
          </div>
          <div className="field"><label htmlFor="t-details">Details</label><AutoGrowTextarea id="t-details" data-testid="task-details" value={edit.details ?? ''} onChange={(e) => setEdit({ ...edit, details: e.target.value })} /></div>
          {clash.length > 0 && <div className="banner warning" role="status" data-testid="task-edit-clash">Someone else changed {fieldList(clash)} while you were editing.</div>}
          {dirty && (
            <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-start' }}>
              <button className="btn primary sm" data-testid="task-save" onClick={() => void saveEdit()}><Save size={13} /> Save changes</button>
              <button className="btn ghost sm" data-testid="task-discard" onClick={() => { const cur = formOf(t); setBase({ form: cur, updatedAt: t.updatedAt }); setEdit(cur); setClash([]); }}>Discard</button>
            </div>
          )}
        </>
      ) : (
        <>
          <h2 style={{ margin: 0 }}>{t.title}</h2>
          <dl className="kv">
            <dt>Assigned to</dt><dd data-testid="task-assigned-to">{t.assignee ? <>{ASSIGNEE_ICON[t.assignee.type]} {t.assignee.name}{t.assignee.type !== 'user' ? <span className="faint"> · every member’s</span> : null}</> : 'Nobody'}</dd>
            <dt>Deadline</dt><dd>{t.dueAt ? dateTime(t.dueAt) : '—'}</dd>
          </dl>
          {t.details && <p className="task-details-text" data-testid="task-details-text">{detailParts(t.details).map((p, i) => p.to ? <Link key={i} to={p.to}>{p.text}</Link>
            : p.href ? <a key={i} href={p.href} target="_blank" rel="noreferrer">{p.text}</a> : p.text)}</p>}
        </>
      )}
      <dl className="kv">
        {t.subject && <><dt>About</dt><dd>{SUBJECT_ICON[t.subject.type]} <Link to={t.subject.link}>{t.subject.name}</Link></dd></>}
        {t.takenFrom && <><dt>Taken from</dt><dd data-testid="task-taken-from">{ASSIGNEE_ICON[t.takenFrom.type]} {t.takenFrom.name}</dd></>}
        <dt>Created</dt><dd>{t.createdBy?.name ?? 'PRISM'} · {relative(t.createdAt)}</dd>
        {t.completedAt && <><dt>Done</dt><dd>{t.completedBy ?? '—'} · {relative(t.completedAt)}</dd></>}
      </dl>

      {shared && <SharedCollection id={t.subject!.id} />}

      <section className="panel-card">
        <p className="section-label">Notes</p>
        <Thread loaded={notes.data !== undefined} messages={(notes.data?.items ?? []).map((n) => ({
          id: n.id, testId: 'task-note', author: n.author ?? 'Someone', mine: n.mine, createdAt: n.createdAt, body: n.body, event: Boolean(n.statusTo),
        }))} />
        <NoteComposer value={note} onChange={setNote} inputTestId="task-note-input"
          onAdd={() => void api(`/api/v1/tasks/${t.id}/notes`, { method: 'POST', json: { body: note } }).then(() => { setNote(''); return refresh(); })} />
      </section>
    </div>
  );
}

/** Someone else's collection handed over with a task: what is in it. */
function SharedCollection({ id }: { id: string }) {
  const q = useCollectionItems(id);
  if (!q.data) return null;
  return (
    <section className="panel-card" data-testid="shared-collection">
      <p className="section-label">In the collection</p>
      {q.data.assets.map((a) => <div className="loc-row" key={a.id}><Film size={13} /><Link to={`/assets/${a.id}`}>{a.title}</Link></div>)}
      {q.data.sequences.map((s) => <div className="loc-row" key={s.id}><Clapperboard size={13} /><Link to={`/create/${s.id}`}>{s.name}</Link></div>)}
      {!q.data.assets.length && !q.data.sequences.length && <p className="faint" style={{ margin: 0 }}>It is empty.</p>}
      <LoadMore list={q} shown={q.data.assets.length + q.data.sequences.length} noun="items" />
    </section>
  );
}
